import React, { useRef, useState, useEffect } from 'react';
import { usePracticeContent } from '../hooks/usePracticeContent';

const REDUCED =
  typeof window !== 'undefined' && window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const NAVY = '#002F6C';
const INK = '#3A3530';
const PHOTO_PLACEHOLDER = 'linear-gradient(180deg,#DEDFD6,#AEB98F 46%,#6E7D4A 76%,#3F4A28)';

// one family for all four statements
const STMT = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  letterSpacing: '-.035em',
  lineHeight: 1.08,
  color: INK,
  textAlign: 'center',
};
const BIG = 'clamp(40px,7vw,96px)';
const BAR = 'clamp(30px,4.8vw,64px)';

function useReveal() {
  const ref = useRef(null);
  const [inView, setInView] = useState(!!REDUCED);
  useEffect(() => {
    if (REDUCED) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } });
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

const PracticeValues = () => {
  const { data } = usePracticeContent();
  const images = (data && data.images) || {};
  const garden = images.values_garden || { url: '', caption: '' };
  const wild = images.values_wild || { url: '', caption: '' };

  const [r1, in1] = useReveal();
  const [r2, in2] = useReveal();
  const [r3, in3] = useReveal();
  const [r4, in4] = useReveal();

  const photoBg = (url) => (url
    ? { backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: PHOTO_PLACEHOLDER });

  return (
    <>
      <style>{`
        .pv-slide{ position:relative; min-height:88vh; display:flex; }
        .pv-rev{ opacity:0; transform:translateY(20px); transition:opacity .8s cubic-bezier(.2,.8,.2,1), transform .8s cubic-bezier(.2,.8,.2,1); }
        .pv-rev.in{ opacity:1; transform:none; }
        /* the veil: its own layer behind the text, never blocks pointers */
        .pv-veil{ position:absolute; inset:0; background:rgba(248,248,247,.64); backdrop-filter:blur(2px);
          -webkit-backdrop-filter:blur(2px); pointer-events:none; opacity:1; transition:opacity .5s ease; }
        @media (hover:hover){ .pv-photo:hover .pv-veil{ opacity:.22; } }
        @media (max-width:760px){ .pv-slide{ min-height:70vh; } }
        @media (prefers-reduced-motion: reduce){
          .pv-rev{ opacity:1; transform:none; transition:none; }
          .pv-veil{ transition:none; }
          .pv-photo:hover .pv-veil{ opacity:1; }
        }
      `}</style>

      {/* Slide 1 — off-white */}
      <section ref={r1} className="pv-slide" style={{ background: '#F8F8F7', alignItems: 'center' }} data-testid="practice-values-slide-1">
        <div className={`pr-spine pv-rev${in1 ? ' in' : ''}`}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '11.5px', letterSpacing: '0.3em', color: '#4A6A9C', textTransform: 'lowercase', textAlign: 'center', marginBottom: 28 }} data-testid="practice-values-kicker">
            how we practice
          </p>
          <h2 style={{ ...STMT, fontSize: BIG }} data-testid="practice-values-statement-1">
            <span style={{ color: NAVY }}>research</span><br />before drawing.
          </h2>
        </div>
      </section>

      {/* Slide 2 — photo, statement in a full-width bar along the bottom */}
      <section ref={r2} className="pv-slide pv-photo" style={{ ...photoBg(garden.url), alignItems: 'flex-end' }} data-testid="practice-values-slide-2">
        <div className="pv-veil" aria-hidden="true" data-testid="practice-values-veil-2" />
        <div style={{ position: 'relative', width: '100%', padding: '38px 0' }} data-testid="practice-values-bar-2">
          <div className={`pr-spine pv-rev${in2 ? ' in' : ''}`}>
            <h2 style={{ ...STMT, fontSize: BAR }} data-testid="practice-values-statement-2">
              the <span style={{ color: NAVY }}>garden</span> comes first.
            </h2>
          </div>
        </div>
      </section>

      {/* Slide 3 — photo, whole slide veiled, line centered */}
      <section ref={r3} className="pv-slide pv-photo" style={{ ...photoBg(wild.url), alignItems: 'center' }} data-testid="practice-values-slide-3">
        <div className="pv-veil" aria-hidden="true" data-testid="practice-values-veil-3" />
        <div className={`pr-spine pv-rev${in3 ? ' in' : ''}`} style={{ position: 'relative' }}>
          <h2 style={{ ...STMT, fontSize: BIG }} data-testid="practice-values-statement-3">
            disciplined enough<br />to <span style={{ color: NAVY }}>stay wild.</span>
          </h2>
        </div>
      </section>

      {/* Slide 4 — off-white */}
      <section ref={r4} className="pv-slide" style={{ background: '#F8F8F7', alignItems: 'center' }} data-testid="practice-values-slide-4">
        <div className={`pr-spine pv-rev${in4 ? ' in' : ''}`}>
          <h2 style={{ ...STMT, fontSize: BIG }} data-testid="practice-values-statement-4">
            we don't pay for awards.<br /><span style={{ color: NAVY }}>we don't buy attention.</span>
          </h2>
        </div>
      </section>
    </>
  );
};

export default PracticeValues;
